/**
 * 논서술형 챕터 그만풀기(STOPPED)/일시정지(PAUSED) 임시저장 + 탭 종료 직전 긴급 전송(keepalive).
 * 정상 흐름은 `postEssayChapterSuspend`를 쓰고, 탭·브라우저를 닫는 순간에는 `fetch(keepalive:true)`
 * 버전을 쓴다. axios 인스턴스는 페이지 종료 시점에 요청이 취소될 수 있어서다.
 */
import api from "pages/api/axiosInstance";
import { getApiBaseUrl } from "@/utils/getApiBaseUrl";
import {
  HeartbeatRequest,
  SuspendChapterRequest,
  SuspendChapterResponse,
} from "./types";

interface BaseResponse<T> {
  isSuccess: boolean;
  code: number;
  message: string;
  result: T;
}

/**
 * 그만풀기/일시정지 bulk 임시저장.
 * `POST /app/essay-learning/chapters/{id}/suspend`
 *
 * 에러: 400(제출완료 챕터), 404(미존재)
 */

export const postEssayChapterSuspend = async (
  chapterId: number,
  payload: SuspendChapterRequest
): Promise<SuspendChapterResponse | null> => {
  try {
    const res = await api.post<BaseResponse<SuspendChapterResponse>>(
      `/app/essay-learning/chapters/${chapterId}/suspend`,
      payload
    );
    return res.data.result;
  } catch {
    // 임시저장 실패가 사용자 흐름(홈 이동 등)을 막지 않도록 호출부가 무시 가능.
    return null;
  }
};

/**
 * 이어풀기. 재접속 복원을 마친 뒤 호출해 서버의 임시중단 상태(PAUSED/STOPPED/CLOSED)를 소비한다.
 * `POST /app/essay-learning/chapters/{id}/resume`. 실패해도 풀이는 계속되므로 호출부가 무시 가능.
 */
export const postEssayChapterResume = async (chapterId: number): Promise<boolean> => {
  try {
    await api.post<BaseResponse<boolean>>(`/app/essay-learning/chapters/${chapterId}/resume`);
    return true;
  } catch {
    return false;
  }
};

/**
 * 문항 화면 heartbeat. 현재 미션의 남은 초를 주기적으로 남긴다.
 * 기기 꺼짐·브라우저 튕김처럼 종료 신호 없이 끊기면 이 값이 마지막 남은 시간이 된다.
 * `POST /app/essay-learning/chapters/{id}/heartbeat`
 */
export const postEssayChapterHeartbeat = async (
  chapterId: number,
  payload: HeartbeatRequest
): Promise<void> => {
  try {
    await api.post<BaseResponse<boolean>>(
      `/app/essay-learning/chapters/${chapterId}/heartbeat`,
      payload
    );
  } catch {
    // best-effort.
  }
};

/** 탭/브라우저 종료 직전 suspend 긴급 전송(fetch keepalive). axios는 종료 시점에 취소될 수 있다. */

export const sendEssaySuspendKeepalive = (
  chapterId: number,
  payload: SuspendChapterRequest
): void => {
  if (typeof window === "undefined") return;
  try {
    const token = sessionStorage.getItem("accessToken");
    fetch(`${getApiBaseUrl()}/app/essay-learning/chapters/${chapterId}/suspend`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // best-effort. 종료 직전 전송 실패는 무시.
  }
};

/** 탭/브라우저 종료 직전 essay-audit 로그 긴급 전송(fetch keepalive). */

export const sendEssayAuditKeepalive = (
  essayChapterId: number,
  eventType: string,
  metadata: unknown
): void => {
  if (typeof window === "undefined") return;
  try {
    const token = sessionStorage.getItem("accessToken");
    fetch(`${getApiBaseUrl()}/app/logs/essay-audit`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        essayChapterId,
        eventType,
        eventAt: Date.now(),
        metadata: metadata ?? null,
      }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // best-effort.
  }
};
